import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { colors } from '../theme/theme';
import TestInstanceBadge from '../components/TestInstanceBadge';
import ForgotPasswordDialog from '../components/ForgotPasswordDialog';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError(
        'The email address is not in a valid format. Please enter it like name@example.com, with no spaces.',
      );
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setIsLoading(true);

    try {
      await login(trimmedEmail, password);
      // Always land on /dashboard. AdminGuard captures the pre-logout path in
      // location.state.from, but that path belongs to the PREVIOUS session — if
      // a different studio just signed in, replaying e.g. /studios/<prevId>
      // shows the old studio's profile under the new token. Dashboard is the one
      // safe destination for any account.
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      // Nest validation errors arrive as an array of messages.
      const message = Array.isArray(err.message) ? err.message.join(' ') : err.message;
      setError(message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Logo Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <TestInstanceBadge />
            </Box>
            <Box
              component="img"
              src="/logo.png"
              alt="Sweat Social Logo"
              sx={{
                width: 120,
                height: 120,
                mb: 2,
                borderRadius: '50%',
                backgroundColor: 'white',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              }}
            />
            <Typography
              variant="h3"
              sx={{
                color: colors.primary,
                fontWeight: 700,
                letterSpacing: '1px',
                mb: 1
              }}
            >
              Sweat Social
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: colors.textSecondary,
                fontWeight: 400,
                letterSpacing: '0.5px'
              }}
            >
              Admin Panel
            </Typography>
          </Box>

          {/* Login Form */}
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 4,
              borderRadius: 3,
              backgroundColor: colors.white,
              border: `2px solid ${colors.primary}`,
              boxShadow: `0 8px 32px rgba(244, 143, 180, 0.2)`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography component="h1" variant="h4" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
                Sign In
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Enter your credentials to access the admin panel
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sweatsocial.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: colors.white,
                    '& fieldset': {
                      borderColor: colors.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: colors.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.textSecondary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  },
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: colors.white,
                    '& fieldset': {
                      borderColor: colors.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: colors.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary,
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.textSecondary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: colors.primary,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
                  },
                  boxShadow: '0 4px 12px rgba(244, 143, 180, 0.4)',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => setForgotOpen(true)}
                  sx={{
                    textTransform: 'none',
                    color: colors.primary,
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Forgot password?
                </Button>
              </Box>
            </Box>

            {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                Default Admin Credentials:
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                Email: admin@sweatsocial.com | Password: admin123
              </Typography>
            </Box> */}
          </Paper>
        </Box>
      </Container>

      <ForgotPasswordDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        initialEmail={email}
      />
    </Box>
  );
};

export default LoginPage;